import React from 'react';
import styled from 'styled-components';

import { animations, colors, ColorType, utils } from '../../../core';
import Heading from '../Heading';
import Icon from '../Icon';
import Text from '../Text';

export interface NotificationProps {
  children: React.ReactNode;
  className?: string;
  width?: string;
  appearance: 'success' | 'warning' | 'error' | 'info';
  transitionState?: 'entering' | 'entered' | 'exiting' | 'exited';
}

const Box = styled.div<{ className: string }>`
  align-items: flex-start;
  background-color: ${colors.white};
  border: 1px solid ${colors.smoke};
  display: flex;
  flex-direction: row;
  padding: 10px 16px;
  position: relative;
  margin-bottom: 16px;

  &::after {
    content: '';
    background-color: ${({ color }) => color};
    bottom: -1px;
    display: block;
    height: 4px;
    left: -1px;
    position: absolute;
    right: -1px;
  }

  &.entering {
    animation: ${animations.fadeAndSlideInFromRight} 220ms forwards ease-out;
  }

  &.exiting {
    animation: ${animations.fadeAndSlideOutToRight} 220ms forwards ease-in;
  }
`;

const Title = styled(Heading).attrs({
  size: 7,
})`
  margin-bottom: 2px;
`;

const Body = styled(Text)`
  margin-bottom: 0;
`;

const TextContainer = styled.div`
  flex: 1;
  margin-left: 10px;
`;

const getColor = (colorKey: string) =>
  colorKey in colors ? colors[colorKey as keyof ColorType] : colors.ash;

const Notification = ({ 
  children, 
  className, 
  appearance, 
  transitionState,
  width,
}: NotificationProps) => (
  <Box
    className={
      className && transitionState
        ? utils.mergeClassNames([className, transitionState]).join(' ')
        : ''
    }
    style={{ width: width || '398px' }}
    color={getColor(appearance)}
  >
    <Icon type={appearance} color={getColor(appearance)} />
    <TextContainer>{children}</TextContainer>
  </Box>
);

Notification.defaultProps = {
  appearance: 'success',
} as const;

Notification.Title = Title;
Notification.Body = Body;
Notification.create = (title: string, body: string) => (
  <React.Fragment>
    <Title>{title}</Title>
    <Body>{body}</Body>
  </React.Fragment>
);

export default Notification;
